import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { GlobalModeOverlay } from "@/components/auth/GlobalModeOverlay";
import { BillingDataGate } from "@/components/billing/BillingDataGate";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { usePrefetchCategories } from "@/hooks/useActiveCategories";
import { TrackingScripts } from "@/components/TrackingScripts";
import { CookieConsent } from "@/components/CookieConsent";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import Index from "./pages/Index";
import Register from "./pages/auth/Register";
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import VisibilityPackages from "./pages/VisibilityPackages";
import SubscriptionPlans from "./pages/SubscriptionPlans";
import Subscription from "./pages/dashboard/Subscription";
import BecomeProfessional from "./pages/BecomeProfessional";
import Affiliate from "./pages/dashboard/Affiliate";
import Creator from "./pages/dashboard/Creator";
import Earnings from "./pages/dashboard/Earnings";
import Preferences from "./pages/dashboard/Preferences";
import ProfileEdit from "./pages/dashboard/ProfileEdit";
import ProfessionalProfile from "./pages/ProfessionalProfile";
import CompanyProfile from "./pages/CompanyProfile";
import Professionals from "./pages/Professionals";
import ScreeningAdmin from "./pages/admin/ScreeningAdmin";
import CreatorReview from "./pages/admin/CreatorReview";
import ReviewsModeration from "./pages/admin/ReviewsModeration";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminCoupons from "./pages/admin/Coupons";
import AdminBadges from "./pages/admin/Badges";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminSubscriptions from "./pages/admin/Subscriptions";
import AdminEvents from "./pages/admin/Events";
import AdminJobs from "./pages/admin/Jobs";
import AdminPayouts from "./pages/admin/Payouts";
import AdminSettings from "./pages/admin/Settings";
import AdminCategories from "./pages/admin/Categories";
import AdminBlockedIPs from "./pages/admin/BlockedIPs";
import AdminBlog from "./pages/admin/Blog";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import EventCreate from "./pages/dashboard/EventCreate";
import EventsManage from "./pages/dashboard/EventsManage";
import EventParticipants from "./pages/dashboard/EventParticipants";
import SavedSearches from "./pages/dashboard/SavedSearches";
import ReviewsManage from "./pages/dashboard/ReviewsManage";
import UserNotifications from "./pages/dashboard/Notifications";
import Sitemap from "./pages/Sitemap";
import Monitoring from "./pages/admin/Monitoring";
import AdminNotifications from "./pages/admin/Notifications";
import AdminReports from "./pages/admin/Reports";
import AdminPartnerships from "./pages/admin/Partnerships";
import Partners from "./pages/Partners";
import PartnerDetail from "./pages/PartnerDetail";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import CookiePolicy from "./pages/CookiePolicy";
import HowItWorks from "./pages/HowItWorks";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Maintenance from "./pages/Maintenance";
import ComingSoon from "./pages/ComingSoon";
import NotFound from "./pages/NotFound";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import CompanyJobs from "./pages/dashboard/CompanyJobs";
import JobCreate from "./pages/dashboard/JobCreate";
import JobApplications from "./pages/dashboard/JobApplications";
import Search from "./pages/Search";
import PaymentSuccess from "./pages/PaymentSuccess";
import Install from "./pages/Install";
import Onboarding from "./pages/Onboarding";
import AdminUserInsights from "./pages/admin/UserInsights";

const queryClient = new QueryClient();

// Component to initialize affiliate tracking at app level
const AffiliateTracker = () => {
  useAffiliateTracking();
  return null;
};

// Component to prefetch categories at app init for faster load
const CategoryPrefetcher = () => {
  usePrefetchCategories();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <AffiliateTracker />
        <CategoryPrefetcher />
        <TrackingScripts />
        <AuthProvider>
          <GlobalModeOverlay />
          <BillingDataGate>
            <Routes>
              <Route path="/maintenance" element={<Maintenance />} />
              <Route path="/coming-soon" element={<ComingSoon />} />
              <Route path="/" element={<Index />} />
              <Route path="/auth/register" element={<Register />} />
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              {/* Onboarding - requires auth */}
              <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
              {/* Protected Dashboard Routes */}
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/dashboard/subscription" element={<RequireAuth><Subscription /></RequireAuth>} />
              <Route path="/dashboard/profile-edit" element={<RequireAuth><ProfileEdit /></RequireAuth>} />
              <Route path="/dashboard/events" element={<RequireAuth><EventsManage /></RequireAuth>} />
              <Route path="/dashboard/events/create" element={<RequireAuth><EventCreate /></RequireAuth>} />
              <Route path="/dashboard/events/:id/edit" element={<RequireAuth><EventCreate /></RequireAuth>} />
              <Route path="/dashboard/events/:id/participants" element={<RequireAuth><EventParticipants /></RequireAuth>} />
              <Route path="/dashboard/searches" element={<RequireAuth><SavedSearches /></RequireAuth>} />
              <Route path="/dashboard/reviews" element={<RequireAuth><ReviewsManage /></RequireAuth>} />
              <Route path="/dashboard/affiliate" element={<RequireAuth><Affiliate /></RequireAuth>} />
              <Route path="/dashboard/creator" element={<RequireAuth><Creator /></RequireAuth>} />
              <Route path="/dashboard/earnings" element={<RequireAuth><Earnings /></RequireAuth>} />
              <Route path="/dashboard/preferences" element={<RequireAuth><Preferences /></RequireAuth>} />
              <Route path="/dashboard/notifications" element={<RequireAuth><UserNotifications /></RequireAuth>} />
              <Route path="/dashboard/company/jobs" element={<RequireAuth><CompanyJobs /></RequireAuth>} />
              <Route path="/dashboard/company/jobs/create" element={<RequireAuth><JobCreate /></RequireAuth>} />
              <Route path="/dashboard/company/jobs/:id/edit" element={<RequireAuth><JobCreate /></RequireAuth>} />
              <Route path="/dashboard/company/jobs/:id/applications" element={<RequireAuth><JobApplications /></RequireAuth>} />
              
              {/* Protected Admin Routes */}
              <Route path="/admin" element={<RequireAuth requireAdmin><AdminDashboard /></RequireAuth>} />
              <Route path="/admin/dashboard" element={<RequireAuth requireAdmin><AdminDashboard /></RequireAuth>} />
              <Route path="/admin/notifications" element={<RequireAuth requireAdmin><AdminNotifications /></RequireAuth>} />
              <Route path="/admin/partnerships" element={<RequireAuth requireAdmin><AdminPartnerships /></RequireAuth>} />
              <Route path="/admin/reports" element={<RequireAuth requireAdmin><AdminReports /></RequireAuth>} />
              <Route path="/admin/users" element={<RequireAuth requireAdmin><AdminUsers /></RequireAuth>} />
              <Route path="/admin/coupons" element={<RequireAuth requireAdmin><AdminCoupons /></RequireAuth>} />
              <Route path="/admin/badges" element={<RequireAuth requireAdmin><AdminBadges /></RequireAuth>} />
              <Route path="/admin/analytics" element={<RequireAuth requireAdmin><AdminAnalytics /></RequireAuth>} />
              <Route path="/admin/events" element={<RequireAuth requireAdmin><AdminEvents /></RequireAuth>} />
              <Route path="/admin/jobs" element={<RequireAuth requireAdmin><AdminJobs /></RequireAuth>} />
              <Route path="/admin/payouts" element={<RequireAuth requireAdmin><AdminPayouts /></RequireAuth>} />
              <Route path="/admin/subscriptions" element={<RequireAuth requireAdmin><AdminSubscriptions /></RequireAuth>} />
              <Route path="/admin/settings" element={<RequireAuth requireAdmin><AdminSettings /></RequireAuth>} />
              <Route path="/admin/screening" element={<RequireAuth requireAdmin><ScreeningAdmin /></RequireAuth>} />
              <Route path="/admin/monitoring" element={<RequireAuth requireAdmin><Monitoring /></RequireAuth>} />
              <Route path="/admin/reviews" element={<RequireAuth requireAdmin><ReviewsModeration /></RequireAuth>} />
              <Route path="/admin/creator-review" element={<RequireAuth requireAdmin><CreatorReview /></RequireAuth>} />
              <Route path="/admin/categories" element={<RequireAuth requireAdmin><AdminCategories /></RequireAuth>} />
              <Route path="/admin/blog" element={<RequireAuth requireAdmin><AdminBlog /></RequireAuth>} />
              <Route path="/admin/blocked-ips" element={<RequireAuth requireAdmin><AdminBlockedIPs /></RequireAuth>} />
              <Route path="/admin/user-insights" element={<RequireAuth requireAdmin><AdminUserInsights /></RequireAuth>} />
              
              {/* Public Routes */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/visibility-packages" element={<VisibilityPackages />} />
              <Route path="/subscription-plans" element={<SubscriptionPlans />} />
              <Route path="/become-professional" element={<BecomeProfessional />} />
              <Route path="/professional/:slug" element={<ProfessionalProfile />} />
              <Route path="/company/:slug" element={<CompanyProfile />} />
              <Route path="/professionals" element={<Professionals />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/partners/:id" element={<PartnerDetail />} />
              <Route path="/search" element={<Search />} />
              <Route path="/events" element={<Events />} />
              <Route path="/event/:slug" element={<EventDetail />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/jobs/:slug" element={<JobDetail />} />
              {/* Sitemap Routes - All with inclusia.pro domain */}
              <Route path="/sitemap.xml" element={<Sitemap />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/page-sitemap.xml" element={<Sitemap type="page" />} />
              <Route path="/professional-sitemap.xml" element={<Sitemap type="professional" />} />
              <Route path="/company-sitemap.xml" element={<Sitemap type="company" />} />
              <Route path="/event-sitemap.xml" element={<Sitemap type="event" />} />
              <Route path="/category-sitemap.xml" element={<Sitemap type="category" />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/install" element={<Install />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BillingDataGate>
          <CookieConsent />
          <PWAInstallBanner />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
